import { ProgressBar } from "@pixi/ui";
import { Linear, SteppedEase, gsap } from "gsap";
import { RoughEase } from "gsap/EasePack";
import { Container, IDestroyOptions, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { GameFont } from "../GameFont";
type Timeline = gsap.core.Timeline;

export class RollProgressCounter extends Container {
    private static readonly MAX_ROLLS_VALUE = 50;

    private static readonly COLLECT_ONE_ROLL_ANIMATION_TIME = 0.05;
    private static readonly SUBTRACT_ROLL_ANIMATION_TIME = 0.8;
    private static readonly SUBTRACT_ROLL_POSITION_Y = -150;
    private static readonly MAX_SHAKE_FACTOR = 5;

    private content: Container;
    private progressBar: ProgressBar;
    private rollsCountField: Text;
    private rollsCollectAnimation: Timeline;

    private maxRolls: number;
    private currentRolls: number;

    private shakeFactor: number = 0;

    public constructor(progressHolder: Texture, progressFill: Texture, currentRolls: number, maxRolls: number = currentRolls) {
        super();
        this.currentRolls = currentRolls;
        this.maxRolls = RollProgressCounter.MAX_ROLLS_VALUE;
        this.rollsCollectAnimation = gsap.timeline();
        this.build(progressHolder, progressFill);
    }

    private build(progressHolder: Texture, progressFill: Texture): void {
        this.content = new Container();
        this.addChild(this.content);
        const progressBarBg = new Sprite(progressHolder);
        const progressBarFill = new Sprite(progressFill);
        this.progressBar = new ProgressBar({ bg: progressBarBg, fill: progressBarFill, progress: this.currentRolls * (100 / this.maxRolls) });
        this.progressBar.position.set(-this.progressBar.width * 0.5, -this.progressBar.height * 0.5);
        this.content.addChild(this.progressBar);
        this.rollsCountField = new Text(this.currentRolls.toString(), new ProgressTextStyle());
        this.rollsCountField.anchor.set(0.5, 0.5);
        this.rollsCountField.position.y = 2;
        this.content.addChild(this.rollsCountField);
    }

    public async setProgress(value: number): Promise<void> {
        if (this.currentRolls === value) {
            return;
        }
        const difference = value - this.currentRolls;
        this.currentRolls = value;
        this.updateProgress();
        if (difference < 0) {
            await this.playSubtractRollsAnimation(difference);
        }
    }

    public async incrementProgress(): Promise<void> {
        if (this.shakeFactor < RollProgressCounter.MAX_SHAKE_FACTOR) {
            this.shakeFactor++;
        }
        this.currentRolls++;
        await this.playAddRollsAnimation(1);
        this.updateProgress();
        this.shakeFactor = 0;
    }

    private updateProgress(): void {
        this.progressBar.progress = this.maxRolls > 0 ? this.currentRolls / this.maxRolls * 100 : 0;
        this.rollsCountField.text = this.currentRolls.toString();
    }

    private async playAddRollsAnimation(addRollsValue: number): Promise<void> {
        this.rollsCollectAnimation.clear();
        this.playShakeAnimation(addRollsValue);
        this.rollsCollectAnimation.to(this.progressBar, { progress: (this.currentRolls + addRollsValue) / this.maxRolls * 100, duration: RollProgressCounter.COLLECT_ONE_ROLL_ANIMATION_TIME * addRollsValue, ease: "none" }, "<");
        this.rollsCollectAnimation.to(this.rollsCountField, { text: (this.currentRolls + addRollsValue).toString(), duration: RollProgressCounter.COLLECT_ONE_ROLL_ANIMATION_TIME * addRollsValue, ease: SteppedEase.config(addRollsValue) }, "<");
        await this.rollsCollectAnimation;
        this.rollsCollectAnimation.clear();
    }

    private playShakeAnimation(rollsDifferenceValue: number): void {
        this.rollsCollectAnimation.fromTo(this.content, { x: 0, y: 0 }, {
            x: 1,
            y: this.shakeFactor,
            yoyo: true,
            repeat: 1,
            duration: RollProgressCounter.COLLECT_ONE_ROLL_ANIMATION_TIME * rollsDifferenceValue,
            ease: RoughEase.ease.config({ strength: 12, points: 50, template: Linear.easeNone, taper: "in", randomize: false })
        });
    }

    private async playSubtractRollsAnimation(subtractRollsValue: number): Promise<void> {
        const subtractRollText = new Text(-subtractRollsValue.toString(), new ProgressTextStyle());
        subtractRollText.anchor.set(0.5, 0.5);
        this.content.addChild(subtractRollText);
        this.rollsCollectAnimation.to(subtractRollText.scale, { x: 2, y: 2, duration: RollProgressCounter.SUBTRACT_ROLL_ANIMATION_TIME, ease: "none" });
        this.rollsCollectAnimation.to(subtractRollText, {
            y: RollProgressCounter.SUBTRACT_ROLL_POSITION_Y,
            duration: RollProgressCounter.SUBTRACT_ROLL_ANIMATION_TIME,
            ease: "none",
            onComplete: subtractRollText.destroy.bind(subtractRollText)
        }, "<");
        await this.rollsCollectAnimation;
        this.rollsCollectAnimation.clear();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.rollsCollectAnimation?.kill();
        super.destroy(options);
    }
}

class ProgressTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#000000",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 44,
        }));
    }
}
